import { InventoryAsset } from '../../types';
import { ProgressHandler } from '../Attachments/attachmentsApi';
import { callExternalApi, CallResult } from '../callExternalApi';

export interface ValidationError {
  error: string;
  column: string;
  row: number;
  external_id: string;
  id: string;
  color?: string;
}

export interface ValidationSuccess {
  id: string;
  account_id: string;
  supplier_id: string;
  product_id: string;
  content: InventoryAsset;
  created_at: string;
  updated_at: string;
  external_id: string;
}
export interface FileValidationResponse {
  success: ValidationSuccess[];
  errors: ValidationError[];
  warnings: ValidationError[];
}

export interface FileUploadResponse {
  success: ValidationSuccess[];
}

export async function fetchOutsideInventory(accountId: string): Promise<CallResult<InventoryAsset[]>> {
  if (!accountId?.length) return { data: null, error: { message: 'Must provide account ID to fetch asset' } };

  return await callExternalApi<InventoryAsset[]>({
    url: `api/outside_inventory?account_id=${accountId}`,
    method: 'GET',
    headers: {
      'content-type': 'application/json'
    }
  });
}

export const createOutsideInventory = async (body: InventoryAsset) => {
  return await callExternalApi<InventoryAsset>({
    url: 'api/outside_inventory',
    method: 'POST',
    headers: {
      'content-type': 'application/json'
    },
    data: body
  });
};

export const createBulkOutsideInventory = async (bodies: InventoryAsset[]) => {
  return await callExternalApi<InventoryAsset[]>({
    url: 'api/outside_inventory/bulk',
    method: 'POST',
    headers: {
      'content-type': 'application/json'
    },
    data: { inventory_items: bodies }
  });
};

export const updateOutsideInventory = async (id: string, body: InventoryAsset, supportType = false) => {
  // Backend expects custom_field_definition_id key; the asset response uses definition_id.
  // Transform to the shape upsert_custom_field_values accepts.
  const { custom_fields, ...rest } = body;
  // A row can also carry Related Inventory values, which this write would reject as not the record's own.
  const inventoryFields = custom_fields?.filter((cf) => (cf.entity_type ?? 'Inventory') === 'Inventory');
  const payload = {
    ...rest,
    ...(inventoryFields?.length
      ? {
          custom_fields: inventoryFields.map((cf) => ({
            custom_field_definition_id: cf.definition_id,
            value: cf.value
          }))
        }
      : {})
  };

  return await callExternalApi<InventoryAsset>({
    url: `api/outside_inventory/${id}?upstack_support_managed=${supportType}`,
    method: 'PUT',
    headers: {
      'content-type': 'application/json'
    },
    data: payload
  });
};

export const deleteOutsideInventory = async (id: string) => {
  return await callExternalApi<void>({
    url: `api/outside_inventory/${id}`,
    method: 'DELETE',
    headers: {
      'content-type': 'application/json'
    }
  });
};

export const uploadXLFileValidation = (file: File, progressHandler: ProgressHandler, accountId?: string) => {
  if (!accountId) return { data: null, error: { message: 'Must provide account ID to upload files.' } };
  // Build form object
  const formData = new FormData();
  formData.append('file', file);

  return callExternalApi<FileValidationResponse>({
    url: 'api/oi/validate',
    method: 'post',
    headers: {
      'content-type': 'multipart/form-data',
      AccountScope: accountId
    },
    data: formData,
    onUploadProgress: progressHandler
  });
};

export const uploadXLFile = (file: File, progressHandler: ProgressHandler, accountId?: string) => {
  if (!accountId) return { data: null, error: { message: 'Must provide account ID to upload files.' } };
  // Build form object
  const formData = new FormData();
  formData.append('file', file);

  return callExternalApi<FileUploadResponse>({
    url: 'api/oi/upload',
    method: 'post',
    headers: {
      'content-type': 'multipart/form-data',
      AccountScope: accountId
    },
    data: formData,
    onUploadProgress: progressHandler
  });
};

export const bulkUpdateSupportLevelApi = (ids: string[], upstack_support_managed: boolean) => {
  return callExternalApi<InventoryAsset[]>({
    url: 'api/outside_inventory/bulk/support',
    method: 'PUT',
    headers: {
      'content-type': 'application/json'
    },
    data: { ids, upstack_support_managed }
  });
};
